import { useIntl } from '@leagueplatform/locales';
import { useDocumentTitle } from '@leagueplatform/web-common';
import React from 'react';
import {
  Card,
  HeadingText,
  ParagraphText,
  StackLayout,
  TextAction,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { Switch } from '@leagueplatform/web-common-components';
import { DETAIL, ErrorState } from '@telus-alberta-health-web/shared-module';
import { PageContentWrapper } from 'components/page-content-wrapper.component';
import { PageHeader } from 'components/page-header/page-header.component';
import { useCommunicationPreferences } from 'hooks/use-communication-preferences/use-communication-preferences.hook';
import { EXTERNAL_LINKS } from 'common/links';
import { SECONDARY_CONTENT_MAX_WIDTH } from 'common/constants';
import {
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
  trackAnalyticsEvent,
  usePageViewAnalytics,
} from '@leagueplatform/analytics';

const EmailNotificationsLabel = () => {
  const { formatMessage } = useIntl();
  return (
    <StackLayout>
      <ParagraphText
        css={{
          typography: '$subtitleTwo',
        }}
      >
        {formatMessage({
          id: 'STR_ALLOW_APP_NOTIFICATIONS_SUBHEADING',
        })}
      </ParagraphText>
      <UtilityText css={{ typography: '$caption' }}>
        {formatMessage({ id: 'STR_ALLOW_APP_NOTIFICATIONS_BODY' })}
      </UtilityText>
    </StackLayout>
  );
};

export const NotificationsPage: React.FC = () => {
  const { formatMessage } = useIntl();
  useDocumentTitle(formatMessage({ id: 'STR_NOTIFICATIONS' }));
  usePageViewAnalytics({
    product_area: PRODUCT_AREA.SETTINGS,
    screen_name: SCREEN_NAMES.COMMUNICATION_PREFERENCES,
  });

  const {
    email,
    error,
    isEmailNotificationEnabled,
    isLoading,
    isCurrentlyUpdating,
    showEmail,
    toggleEmailNotification,
  } = useCommunicationPreferences();

  const handleToggleEmailNotification = async () => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.COMMUNICATION_PREFERENCES,
      detail: DETAIL.PUSH_NOTIFICATIONS_TOGGLE,
      toggle_status: !isEmailNotificationEnabled ? 'on' : 'off',
    });
    await toggleEmailNotification();
  };

  const handleEditEmailClick = () => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.COMMUNICATION_PREFERENCES,
      detail: DETAIL.EDIT_EMAIL,
    });
  };

  return (
    <PageContentWrapper css={{ maxWidth: SECONDARY_CONTENT_MAX_WIDTH }}>
      <PageHeader
        showBackButton={false}
        headingText={formatMessage({ id: 'STR_NOTIFICATIONS' })}
      />
      {error && <ErrorState error={error} />}
      {!error && (
        <StackLayout>
          <Card.Elevated css={{ backgroundColor: '$surfaceCardSecondary' }}>
            <Card.Section
              css={{
                gap: '$one',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <HeadingText level="2" size="lg" css={{ padding: '$half' }}>
                {formatMessage({ id: 'STR_APP_NOTIFICATIONS_HEADING' })}
              </HeadingText>
              <Switch
                id="toggle-email-notification"
                loading={isLoading}
                disabled={isCurrentlyUpdating}
                checked={isEmailNotificationEnabled}
                onChange={handleToggleEmailNotification}
                label={<EmailNotificationsLabel />}
                layout="row"
                css={{
                  '.cc-switch-root-wrapper': {
                    display: 'flex',
                    alignSelf: 'start',
                    paddingLeft: '$one',
                  },
                }}
              />
              {showEmail && (
                <StackLayout css={{ padding: '$half', gap: '$half' }}>
                  <UtilityText size="xs" emphasis="subtle">
                    {formatMessage({ id: 'STR_YOU_ARE_GETTING_NOTIFIED_ON' })}
                  </UtilityText>
                  <StackLayout
                    orientation="horizontal"
                    horizontalAlignment="spaceBetween"
                    css={{ width: '100%' }}
                  >
                    <ParagraphText
                      css={{
                        typography: '$bodyTwo',
                      }}
                    >
                      {email}
                    </ParagraphText>
                    <TextAction
                      aria-label={formatMessage({ id: 'STR_EDIT_EMAIL' })}
                      indicator="externalLink"
                      size="medium"
                      href={EXTERNAL_LINKS.ALBERTA_ACCOUNT_SETTINGS}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleEditEmailClick}
                    >
                      {formatMessage({ id: 'STR_EDIT' })}
                    </TextAction>
                  </StackLayout>
                </StackLayout>
              )}
            </Card.Section>
          </Card.Elevated>
        </StackLayout>
      )}
    </PageContentWrapper>
  );
};
